import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import App from './App';
import { initTelemetry } from './lib/telemetry';
import './index.css';

// ---------------------------------------------------------------------------
// Telemetry bootstrap
// ---------------------------------------------------------------------------
//
// Wire Application Insights as early as possible so unhandled errors during
// the SPA mount are still captured. `VITE_APPINSIGHTS_CONNECTION_STRING` is
// the only Vite-time env var exposed to the client (Vite blocks bare names
// for security); when unset (local dev), `initTelemetry` returns a no-op
// client and no SDK code runs.
//
// We store the client on `window` so the App tree can pick it up without
// having to thread it through props or context. The cast to `unknown` plus
// re-cast keeps strict typing intact while we extend the global namespace.
const telemetry = initTelemetry(
  import.meta.env.VITE_APPINSIGHTS_CONNECTION_STRING,
);
(window as unknown as { __telemetry?: typeof telemetry }).__telemetry =
  telemetry;

// Global error & unhandledrejection sinks — these complement the SDK's
// built-in auto-capture by attaching a `source` dimension so dashboards can
// distinguish bootstrap-time errors from in-app catches.
window.addEventListener('error', (ev) => {
  const err = ev.error instanceof Error ? ev.error : new Error(ev.message);
  telemetry.trackException(err, { source: 'window.error' });
});
window.addEventListener('unhandledrejection', (ev) => {
  const reason = ev.reason;
  const err =
    reason instanceof Error
      ? reason
      : new Error(typeof reason === 'string' ? reason : 'unhandledrejection');
  telemetry.trackException(err, { source: 'unhandledrejection' });
});

// ---------------------------------------------------------------------------
// Google Analytics 4 bootstrap
// ---------------------------------------------------------------------------
//
// VITE_GA_MEASUREMENT_ID が設定されている場合のみ gtag.js を動的ロードする。
// 未設定（ローカル開発など）では何もしないので GA がアプリを壊すことはない。
const gaMeasurementId = import.meta.env.VITE_GA_MEASUREMENT_ID?.trim();
if (gaMeasurementId) {
  const gaScript = document.createElement('script');
  gaScript.async = true;
  gaScript.src = `https://www.googletagmanager.com/gtag/js?id=${gaMeasurementId}`;
  document.head.appendChild(gaScript);

  type GtagWindow = { dataLayer?: unknown[]; gtag?: (...args: unknown[]) => void };
  const w = window as unknown as GtagWindow;
  w.dataLayer ??= [];
  // regular function (not arrow) so `arguments` is accessible — GA4 requires
  // IArguments pushed to dataLayer, not a plain array from rest params
  // eslint-disable-next-line prefer-rest-params
  w.gtag = function gtag() { w.dataLayer!.push(arguments); };
  w.gtag('js', new Date());
  w.gtag('config', gaMeasurementId);
}

const container = document.getElementById('root');
if (!container) {
  // Fail fast if the SPA root mount point is missing from index.html
  throw new Error('Root container #root not found in index.html');
}

createRoot(container).render(
  <StrictMode>
    <App />
  </StrictMode>,
);
